/**
 * Product card wishlist (save) buttons.
 *
 * A personal, client-side "save for later" list: state is stored in the
 * shopper's own browser via localStorage only - nothing is sent to a
 * server and no count or social-proof number is derived from it, it's
 * just a private bookmark. Works across every product grid (collections,
 * homepage, search, recommendations, quick-add modal) via a single
 * document-level click listener, so cards added later (infinite scroll,
 * section re-renders) are covered without extra wiring. If localStorage
 * is unavailable (private browsing, quota, disabled), the button still
 * toggles visually for the current page view - it just won't persist.
 */

(function () {
  const STORAGE_KEY = 'caurz:wishlist';

  function readWishlist() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(ids) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (error) {
      // Nothing to do - button still reflects the toggle for this page view.
    }
  }

  function syncButton(button, ids) {
    const id = button.dataset.productId;
    const saved = Boolean(id) && ids.indexOf(id) !== -1;
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
  }

  function syncAll(root) {
    if (!root.querySelectorAll) return;
    const ids = readWishlist();
    root.querySelectorAll('[data-wishlist-toggle]').forEach((button) => syncButton(button, ids));
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;

    // Buttons sit on top of the card's full-size link overlay - stop the
    // click from also navigating to the product page.
    event.preventDefault();
    event.stopPropagation();

    const id = button.dataset.productId;
    if (!id) return;

    const ids = readWishlist();
    const index = ids.indexOf(id);
    if (index === -1) {
      ids.push(id);
    } else {
      ids.splice(index, 1);
    }
    writeWishlist(ids);
    syncButton(button, ids);
  });

  syncAll(document);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-wishlist-toggle]')) {
          syncButton(node, readWishlist());
        }
        syncAll(node);
      }
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
